import { plataforma } from '@/platform';

/** 1 px @ 96 DPI = 25400 / 96 ≈ 264.583 microns. */
const PX_PARA_MICRONS = 25400 / 96;

export type ResultadoImpressao = {
  ok: boolean;
  cancelado: boolean;
  caminho?: string;
  erro?: string;
};

export type PageSizeMicrons = { width: number; height: number };

async function aguardarRenderizar(): Promise<void> {
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
}

/**
 * Mede a altura visível do primeiro elemento que case com o seletor e
 * devolve um pageSize em microns para passar pro Electron. Útil pra
 * impressoras térmicas: largura fixa (ex.: 58mm) e altura dinâmica.
 *
 * Importante: medir um elemento que já esteja renderizado no MESMO tamanho
 * e fonte que ele terá na impressão (ex.: dentro do modal de pré-visualização,
 * onde aplicamos exatamente as mesmas dimensões do papel térmico). Caso
 * contrário a altura medida pode divergir da real e gerar página em branco.
 */
export function medirElementoComoPageSize(
  seletor: string,
  larguraMm: number,
  bufferMm = 3,
): PageSizeMicrons | undefined {
  const el = document.querySelector(seletor) as HTMLElement | null;
  if (!el) return undefined;
  const alturaPx = el.scrollHeight || el.offsetHeight;
  if (!alturaPx) return undefined;
  const alturaMicrons = Math.ceil(alturaPx * PX_PARA_MICRONS) + bufferMm * 1000;
  return {
    width: Math.round(larguraMm * 1000),
    height: Math.max(10_000, alturaMicrons),
  };
}

/**
 * Adiciona temporariamente uma classe no <body> e abre o diálogo de impressão.
 * Quando `pageSize` é fornecido, força essa dimensão no Electron (em vez de
 * depender do papel padrão do driver) — essencial para impressoras térmicas.
 *
 * No navegador não existe API para impor tamanho de papel, então o
 * `pageSize` é convertido em uma regra `@page` temporária: a maioria dos
 * navegadores respeita, e o que sobra fica a cargo do diálogo do sistema.
 */
export async function imprimirComClasse(
  classe: string,
  opcoes: { pageSize?: PageSizeMicrons } = {},
): Promise<ResultadoImpressao> {
  document.body.classList.add(classe);
  const limparPageSize =
    opcoes.pageSize && !plataforma.capacidades.impressaoTermica
      ? injetarPageSizePadrao(opcoes.pageSize)
      : () => undefined;
  try {
    await aguardarRenderizar();
    if (opcoes.pageSize && plataforma.capacidades.impressaoTermica) {
      return await plataforma.imprimir({
        pageSize: opcoes.pageSize,
        margemZero: true,
      });
    }
    if (opcoes.pageSize) {
      // Web: o diálogo do navegador já foi preparado pelo @page acima.
      return await plataforma.imprimir({});
    }
    const promessa = new Promise<ResultadoImpressao>((resolve) => {
      const limpar = () => {
        window.removeEventListener('afterprint', limpar);
        resolve({ ok: true, cancelado: false });
      };
      window.addEventListener('afterprint', limpar);
    });
    window.print();
    await promessa;
    return { ok: true, cancelado: false };
  } finally {
    document.body.classList.remove(classe);
    limparPageSize();
  }
}

/**
 * Versão sem nome de página do `@page`: aplica o tamanho a toda a
 * impressão. É o que dá pra fazer no navegador, onde não existe API para
 * escolher o papel — o Chrome e o Edge respeitam `size` quando o destino
 * é PDF ou uma impressora com papel compatível.
 */
function injetarPageSizePadrao(pageSize: PageSizeMicrons): () => void {
  const larguraMm = (pageSize.width / 1000).toFixed(3);
  const alturaMm = (pageSize.height / 1000).toFixed(3);
  const style = document.createElement('style');
  style.setAttribute('data-print-pagesize', 'auto');
  style.textContent = `@page { size: ${larguraMm}mm ${alturaMm}mm; margin: 0 !important; }`;
  document.head.appendChild(style);
  return () => {
    if (style.parentNode) style.parentNode.removeChild(style);
  };
}

/**
 * Injeta um `<style>` com `@page` apontando para o nome de página dado e o
 * tamanho exato (em mm). Retorna função pra remover o style depois. Usado
 * antes de chamar `printToPDF` com `preferCSSPageSize: true` — assim o PDF
 * sai com largura/altura exatas, sem depender da conversão de unidades
 * do `pageSize` do Electron.
 */
function injetarPageSize(
  nomePagina: string,
  pageSize: PageSizeMicrons,
): () => void {
  const larguraMm = (pageSize.width / 1000).toFixed(3);
  const alturaMm = (pageSize.height / 1000).toFixed(3);
  const style = document.createElement('style');
  style.setAttribute('data-print-pagesize', nomePagina);
  style.textContent = `@page ${nomePagina} { size: ${larguraMm}mm ${alturaMm}mm; margin: 0 !important; }`;
  document.head.appendChild(style);
  return () => {
    if (style.parentNode) style.parentNode.removeChild(style);
  };
}

/**
 * Gera PDF da janela atual respeitando a classe de impressão. Quando
 * `pageSize` é informado, injeta um `@page` dinâmico com essas dimensões
 * exatas e usa `preferCSSPageSize` no printToPDF — assim o PDF sai com o
 * tamanho real (ex.: 58mm × altura do conteúdo).
 *
 * No web não há como escrever o arquivo direto: a plataforma abre o
 * diálogo de impressão e o usuário escolhe "Salvar como PDF". O `@page`
 * injetado aqui continua valendo, então o resultado sai no mesmo formato.
 */
export async function salvarComoPdf(
  classe: string,
  nomeSugerido: string,
  opcoes: { pageSize?: PageSizeMicrons; pageName?: string } = {},
): Promise<ResultadoImpressao> {
  document.body.classList.add(classe);
  const limparPageSize = !opcoes.pageSize
    ? () => undefined
    : opcoes.pageName && plataforma.capacidades.salvarPdfNativo
      ? injetarPageSize(opcoes.pageName, opcoes.pageSize)
      : injetarPageSizePadrao(opcoes.pageSize);
  try {
    await aguardarRenderizar();
    return await plataforma.salvarPdf({ nomeSugerido });
  } finally {
    document.body.classList.remove(classe);
    limparPageSize();
  }
}
